/* eslint-disable max-len */
import { Formik, useFormikContext } from 'formik';
import { ReactElement, useEffect, useMemo } from 'react';
import { useAppSelector } from '../../../redux/hooks';
import {
  selectLiteracyScreeningDiagnostics,
  selectLiteracyScreeningOptionSets,
  selectLiteracyScreeningSchoolList,
  selectLiteracyScreeningUpdate,
} from '../../../redux/slices/literacyScreeningSlice';
import {
  LiteracyScreeningDiagnosticMeasuresModel,
  LITERACY_SCREENING_DIAGNOSTIC_OPTION_SETS,
} from '../../../types/LiteracyScreeningDiagnosticMeasuresModel';
import { LiteracyScreeningSchoolModel } from '../../../types/LiteracyScreeningSchoolModel';
import { PanelType } from '../../../types/PanelType';
import { SelectOption } from '../../../types/SelectOption';
import {
  LiteracyScreeningDiagnosticMeasuresFormValues,
  LiteracyScreeningDiagnosticMeasuresValidationSchema,
} from '../../../types/Validation/LiteracyScreeningDiagnosticMeasuresValidationSchema';
import { VALIDATION_MESSAGE } from '../../../types/ValidationMessage';
import { formatToDateString } from '../../../utilities/dateUtilities';
import { isSPO } from '../../../utilities/userUtilities';
import FormDropdown from '../../FormInputs/FormDropdown/FormDropdown';
import Panel from '../../Panel/Panel';

const toFormValues = (
  diagnostics: LiteracyScreeningDiagnosticMeasuresModel | null
): LiteracyScreeningDiagnosticMeasuresFormValues => ({
  phonemicAwareness: diagnostics?.phonemicAwareness || [],
  phonics: diagnostics?.phonics || [],
  vocabulary: diagnostics?.vocabulary || [],
  oralLanguage: diagnostics?.oralLanguage || [],
});

const getEnabledOptions = (
  optionSets: Record<string, SelectOption[]>,
  key: string
): SelectOption[] =>
  (optionSets[key] || []).filter((option) => !option.isDisabled);

export const isLiteracyScreeningDiagnosticsPanelEnabled = (
  schools: LiteracyScreeningSchoolModel[]
): boolean =>
  (schools || []).some(
    (school) =>
      school?.beginningOfYearNotificationDate != null &&
      String(school.beginningOfYearNotificationDate).trim() !== ''
  );

export interface LiteracyScreeningDiagnosticsEditState {
  dirty: boolean;
  isValid: boolean;
  values: LiteracyScreeningDiagnosticMeasuresFormValues;
}

interface LiteracyScreeningDiagnosticMeasuresPanelProps {
  onEditStateChange?: (state: LiteracyScreeningDiagnosticsEditState) => void;
}

const DiagnosticsStateReporter = ({
  disabled,
  onEditStateChange,
}: {
  disabled: boolean;
  onEditStateChange?: (state: LiteracyScreeningDiagnosticsEditState) => void;
}): null => {
  const { values, dirty } =
    useFormikContext<LiteracyScreeningDiagnosticMeasuresFormValues>();

  useEffect(() => {
    onEditStateChange?.({
      dirty,
      isValid:
        disabled ||
        LiteracyScreeningDiagnosticMeasuresValidationSchema.isValidSync(values),
      values,
    });
  }, [disabled, dirty, onEditStateChange, values]);

  return null;
};

const DiagnosticMeasuresFormError = ({
  disabled,
}: {
  disabled: boolean;
}): ReactElement | null => {
  const { values } =
    useFormikContext<LiteracyScreeningDiagnosticMeasuresFormValues>();
  const hasSelection =
    values.phonemicAwareness.length +
    values.phonics.length +
    values.vocabulary.length +
    values.oralLanguage.length >
    0;

  if (disabled || hasSelection) {
    return null;
  }

  return (
    <p
      className="literacy-screening-diagnostics-error"
      data-testid="literacy-screening-diagnostics-error"
    >
      *{VALIDATION_MESSAGE.LITERACY_SCREENING_DIAGNOSTIC_MEASURES_REQUIRED}
    </p>
  );
};

const LiteracyScreeningDiagnosticMeasuresPanel = ({
  onEditStateChange,
}: LiteracyScreeningDiagnosticMeasuresPanelProps): ReactElement => {
  const diagnostics = useAppSelector(selectLiteracyScreeningDiagnostics);
  const optionSets = useAppSelector(selectLiteracyScreeningOptionSets);
  const schoolList = useAppSelector(selectLiteracyScreeningSchoolList);
  const literacyScreeningUpdate = useAppSelector(selectLiteracyScreeningUpdate);
  const isEnabled =
    isLiteracyScreeningDiagnosticsPanelEnabled(schoolList) &&
    literacyScreeningUpdate &&
    !isSPO();
  const initialValues = useMemo(() => toFormValues(diagnostics), [diagnostics]);
  const lastUpdatedBy = diagnostics?.updatedBy || '';
  const lastUpdatedDate = diagnostics?.updatedDateTime
    ? formatToDateString(diagnostics.updatedDateTime)
    : '';

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={LiteracyScreeningDiagnosticMeasuresValidationSchema}
      onSubmit={() => undefined}
    >
      <>
        <DiagnosticsStateReporter
          disabled={!isEnabled}
          onEditStateChange={onEditStateChange}
        />
        <Panel
          panelType={PanelType.INFO}
          heading="Diagnostic Measures"
          className="literacy-screening-diagnostics-panel"
        >
          <p className="literacy-screening-diagnostics-instruction">
            <span className="required-asterisk">*</span>
            <strong>From Section C</strong>: Select the diagnostic(s) used for
            students who score <strong>below benchmark</strong> in a specific
            component of the screener.
          </p>
          <DiagnosticMeasuresFormError disabled={!isEnabled} />
          <div className="literacy-screening-diagnostics-grid">
            <h3 className="literacy-screening-diagnostics-section-heading">
              Word Recognition
            </h3>
            <div
              className="literacy-screening-diagnostics-last-updated"
              data-testid="literacy-screening-diagnostics-last-updated"
            >
              <div className="literacy-screening-diagnostics-last-updated-row">
                <span className="literacy-screening-diagnostics-last-updated-label">
                  Last Updated By
                </span>
                <span data-testid="literacy-screening-diagnostics-updated-by">
                  {lastUpdatedBy}
                </span>
              </div>
              <div className="literacy-screening-diagnostics-last-updated-row">
                <span className="literacy-screening-diagnostics-last-updated-label">
                  Last Updated Date
                </span>
                <span data-testid="literacy-screening-diagnostics-updated-date">
                  {lastUpdatedDate}
                </span>
              </div>
            </div>
            <FormDropdown
              displayName="Phonemic Awareness"
              field="phonemicAwareness"
              isMulti={true}
              disabled={!isEnabled}
              hideSelectedOptions={false}
              closeMenuOnSelect={false}
              options={getEnabledOptions(
                optionSets,
                LITERACY_SCREENING_DIAGNOSTIC_OPTION_SETS.phonemicAwareness
              )}
            />
            <FormDropdown
              displayName="Phonics"
              field="phonics"
              isMulti={true}
              disabled={!isEnabled}
              hideSelectedOptions={false}
              closeMenuOnSelect={false}
              options={getEnabledOptions(
                optionSets,
                LITERACY_SCREENING_DIAGNOSTIC_OPTION_SETS.phonics
              )}
            />
            <h3 className="literacy-screening-diagnostics-section-heading">
              Language Comprehension
            </h3>
            <div aria-hidden="true" />
            <FormDropdown
              displayName="Vocabulary"
              field="vocabulary"
              isMulti={true}
              disabled={!isEnabled}
              hideSelectedOptions={false}
              closeMenuOnSelect={false}
              options={getEnabledOptions(
                optionSets,
                LITERACY_SCREENING_DIAGNOSTIC_OPTION_SETS.vocabulary
              )}
            />
            <FormDropdown
              displayName="Oral Language"
              field="oralLanguage"
              isMulti={true}
              disabled={!isEnabled}
              hideSelectedOptions={false}
              closeMenuOnSelect={false}
              options={getEnabledOptions(
                optionSets,
                LITERACY_SCREENING_DIAGNOSTIC_OPTION_SETS.oralLanguage
              )}
            />
          </div>
        </Panel>
      </>
    </Formik>
  );
};

export default LiteracyScreeningDiagnosticMeasuresPanel;
